// Bailey companion hover — custom AABB hit-test (not a mesh raycast).
//
// Bailey's a skinned-mesh dog wrapped in an outer Group; we hit-test
// against a Box3 computed from the wrapper position + a fixed extent,
// not against the skinned mesh itself. Cheap, deterministic, and good
// enough at the size of a medium dog.
//
// E engages Bailey for command interactions (1-8 digit commands handled
// by Bailey itself elsewhere in stage.js; R releases). Engagement isn't a
// modal claim in the fixture sense — Bailey doesn't suppress fixture
// hovers and vice-versa, but THIS hover kind is suppressed by every
// fixture modal AND every fixture hover (you can't accidentally engage
// Bailey while aiming at something else). Lowest precedence so all
// fixture state has been set by the time this runs.

import * as THREE from "../../../vendor/three.module.js";
import { registerInteractable } from "../_interactable-registry.js";

const _hitBox = new THREE.Box3(), _hitPt = new THREE.Vector3();

export const baileyKind = {
	id: "bailey",
	precedence: 10,
	// ASK-137 §84 — THIS KIND'S OWN PROMPT AND SUBJECT. `stage.js` used to enumerate every kind by
	// hand, twice (`_updateEHint`'s hover ladder and `_hoveredProduct`), which is exactly what the
	// registry's own header says a registry exists to end. The prompt a shopper reads and the product
	// it names belong where the rest of this kind's behaviour already is.
	hoverHint: '<kbd>E</kbd> Call',
	productOf(stage, active) {
		return null;
	},
	hoverTitle() { return "Bailey"; },

	suppressedBy(stage) {
		if (!stage.bailey) return true;
		// Any box modal/hero state.
		if (stage.boxState !== "none" && stage.boxState !== "closed") return true;
		// Any fixture modal claimed.
		if (stage.activeFixtureHero) return true;
		if (stage.activeVinylBin)    return true;
		if (stage.activeStackTable)  return true;
		// Any fixture hover that's eaten the crosshair (so the player can't
		// accidentally engage Bailey while aiming through him at a fixture).
		if (stage.hoveredPileComic)  return true;
		if (stage.hoveredBook)       return true;
		if (stage.spinnerRack?.hoveredComic) return true;
		if (stage.hoveredWallComic)  return true;
		if (stage.hoveredVinyl)      return true;
		if (stage.hoveredSeatedItem) return true;   // ASK-154 §5.10-5 — a seated product under the crosshair wins (one hover-active)
		return false;
	},

	// ASK-162 — he can stand IN FRONT of a product: the distance to his box along the ray (null = not under it, or a
	// modal holds the frame), and which meshes are his (so the occlusion test skips them).
	frontDistance(stage) {
		if (!stage.bailey || (stage.boxState !== "none" && stage.boxState !== "closed") || stage.activeFixtureHero || stage.activeVinylBin || stage.activeStackTable) return null;
		const p = stage.raycaster.ray.intersectBox(stage.bailey.getWorldBox(_hitBox), _hitPt);
		return p ? p.distanceTo(stage.raycaster.ray.origin) : null;
	},
	owns(stage, o) { for (let n = o; n; n = n.parent) if (n === stage.bailey.root) return true; return false; },

	// Custom hit-test — bypasses the raycast path entirely.
	evaluateActive(stage) {
		stage.bailey.getWorldBox(_hitBox);
		return stage.raycaster.ray.intersectsBox(_hitBox) ? stage.bailey : null;
	},

	commitHover(stage, prev, next) {
		stage.bailey?.setHovered(!!next);
	},

	interactE(stage) {
		if (!stage.bailey) return false;
		return !!stage.bailey.engage();
	},
};
registerInteractable(baileyKind);
